// HqEnvironment — three intensity levels for the HQ backdrop, per the
// approved vision:
//
//   arrival    — cinematic. Rich architecture, visible AI employees,
//                dramatic lighting. Used on the arrival scene.
//                (opacity ~0.20, no blur)
//   department — balanced. HQ recognizable, softly pushed back, UI
//                stays in focus. Default for every view.
//                (opacity ~0.12, subtle blur)
//   focus      — minimal. During long deep-work sessions, the
//                environment fades to almost-nothing so the operator
//                stays productive. Views opt in via useSetHqMode.
//                (opacity ~0.05, heavier blur)
//
// A single Context so every consumer (backdrop, elevator, badge)
// sees the same mode without prop drilling.

import React, { createContext, useCallback, useContext, useMemo, useState } from 'react';

const Ctx = createContext(null);

export const HQ_MODES = { ARRIVAL: 'arrival', DEPARTMENT: 'department', FOCUS: 'focus' };

export function HqEnvironmentProvider({ children, initial = HQ_MODES.DEPARTMENT }) {
  const [mode, setMode] = useState(initial);
  const value = useMemo(() => ({ mode, setMode }), [mode]);
  return <Ctx.Provider value={value}>{children}</Ctx.Provider>;
}

export function useHqEnv() {
  return useContext(Ctx) || { mode: HQ_MODES.DEPARTMENT, setMode: () => {} };
}

// Convenience hook: sets the environment mode for the lifetime of a
// component (e.g., a deep-work view calls useSetHqMode('focus')).
export function useSetHqMode(mode) {
  const { setMode } = useHqEnv();
  React.useEffect(() => {
    setMode(mode);
    return () => setMode(HQ_MODES.DEPARTMENT);
  }, [mode, setMode]);
}

// Intensity recipe per mode — consumed by HqBackdrop. Kept in one
// place so every rendering surface stays in sync.
export const HQ_INTENSITY = {
  [HQ_MODES.ARRIVAL]:    { opacity: 0.20, blur: 0,  scrim: 0.30 },
  [HQ_MODES.DEPARTMENT]: { opacity: 0.12, blur: 1,  scrim: 0.45 },
  [HQ_MODES.FOCUS]:      { opacity: 0.05, blur: 3,  scrim: 0.65 },
};
